import classNames from "classnames";
import { TableUI } from "components/design/TableUI";
import { FormTooltip } from "components/fileTax/FilingReview/ReviewFormControl";
import { useWindowSize } from "hooks/useWindowSize";
import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { ChatQnAResponse } from "types/Models/chatQnA";
import { FileObject } from "types/Models/fileObject";
import { ArrayElement } from "types/utils/ArrayElement";
import { AnswerCell } from "./AnswerCell";
import { DocumentUploadCell } from "./DocumentUploadCell";
import { FilePreviewCell } from "./FilePreviewCell";
import { MobileRequestAnswerRow } from "./MobileRequestAnswerRow";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

type RequestedAnswer = ArrayElement<ChatQnAResponse["requested_answers"]>;

type TableMeta = {
  requestId: string;
  groupId: string;
  isSubmitted: boolean;
  showDocumentsColumn: boolean;
  onAnswerSaved: () => void;
  onFileUploaded: () => void;
};

const createColumn = createColumnHelper<RequestedAnswer>();

const questionColumn = createColumn.accessor("question", {
  cell: (info) => {
    const questionDocuments = info.row.original.question_documents ?? [];
    const tooltip = info.row.original.field_metadata?.tooltip;
    const isRequired = info.row.original.field_metadata?.is_required;

    const question = info.getValue();

    return (
      <div
        className={classNames("t-flex t-flex-col", {
          "t-gap-2": questionDocuments.length > 0,
          "t-justify-center": questionDocuments.length === 0,
        })}
      >
        <div className="t-flex t-items-center t-text-body-sm t-text-text-60">
          <div className="t-inline">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                p({ children }) {
                  return (
                    <p className="t-text-text-30 t-inline t-text-body-sm t-w-full last:t-mb-0 t-mb-2">
                      {children}
                    </p>
                  );
                },
                a({ children, href }) {
                  return (
                    <a href={href} className="t-text-body-sm t-text-purple">
                      {children}
                    </a>
                  );
                },
                ul({ children }) {
                  return (
                    <ul className="t-list-disc t-pl-4 t-text-body-sm t-text-text-30">
                      {children}
                    </ul>
                  );
                },
                ol({ children, start }) {
                  return (
                    <ol
                      start={start}
                      className="t-list-decimal t-pl-4 t-text-body-sm t-text-text-30"
                    >
                      {children}
                    </ol>
                  );
                },
              }}
            >
              {question}
            </ReactMarkdown>

            {isRequired && <span className="t-text-red-50 "> *</span>}
            {tooltip && <FormTooltip tooltipText={tooltip} />}
          </div>
        </div>
        {questionDocuments.length > 0 && (
          <div className="t-flex t-flex-col t-gap-1 t-max-w-96">
            {questionDocuments.map((doc) => (
              <span key={doc.uuid}>
                <FilePreviewCell
                  file={{
                    ...doc,
                    uuid: doc.file_uuid,
                    file_type: doc.file_type as FileObject["file_type"],
                  }}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    );
  },
  header: "Question",
  size: 40,
});

const answerColumn = createColumn.accessor("answer", {
  size: 40,
  cell: (info) => {
    const { requestId, groupId, isSubmitted, onAnswerSaved } = info.table
      .options.meta as TableMeta;

    return (
      <AnswerCell
        uuid={info.row.original.uuid}
        requestId={requestId}
        isSubmitted={isSubmitted}
        groupId={groupId}
        initialAnswer={info.row.original.answer ?? ""}
        fieldMetadata={info.row.original.field_metadata ?? null}
        onSave={onAnswerSaved}
      />
    );
  },
  header: "Answer",
});

const documentsColumn = createColumn.display({
  id: "documents",
  cell: (info) => {
    const { groupId, isSubmitted, onFileUploaded } = info.table.options
      .meta as TableMeta;

    return (
      <DocumentUploadCell
        answerRequestId={info.row.original.uuid}
        groupId={groupId}
        isDisabled={isSubmitted}
        isSubmitted={isSubmitted}
        onUploadSuccess={onFileUploaded}
      />
    );
  },
  header: ({ table }) => {
    const { isSubmitted } = table.options.meta as TableMeta;

    return (
      <div>
        {isSubmitted ? (
          "DOCUMENTS"
        ) : (
          <>
            UPLOAD DOCUMENTS <br />
            (IF ANY)
          </>
        )}
      </div>
    );
  },
  size: 20,
});

type RequestAnswersTableProps = {
  requestedAnswers: RequestedAnswer[];
  requestId: string;
  groupId: string;
  isSubmitted: boolean;
  showDocumentsColumn?: boolean;
  onAnswerSaved: () => void;
  onFileUploaded: () => void;
};

export const RequestAnswersTable = ({
  requestedAnswers,
  requestId,
  groupId,
  isSubmitted,
  showDocumentsColumn = true,
  onAnswerSaved,
  onFileUploaded,
}: RequestAnswersTableProps) => {
  const { isMobile } = useWindowSize();
  const columns = useMemo(
    () =>
      showDocumentsColumn
        ? [questionColumn, answerColumn, documentsColumn]
        : [questionColumn, answerColumn],
    [showDocumentsColumn]
  );

  const meta: TableMeta = useMemo(
    () => ({
      requestId,
      groupId,
      isSubmitted,
      showDocumentsColumn,
      onAnswerSaved,
      onFileUploaded,
    }),
    [
      requestId,
      groupId,
      isSubmitted,
      showDocumentsColumn,
      onAnswerSaved,
      onFileUploaded,
    ]
  );

  const table = useReactTable({
    data: requestedAnswers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta,
  });

  if (isMobile) {
    return (
      <div className="t-flex t-flex-col t-gap-3">
        {table.getRowModel().rows.map((row) => (
          <MobileRequestAnswerRow
            key={row.id}
            row={row}
            isSubmitted={isSubmitted}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-5">
      <TableUI sticky table={table} verticalAlign="top" />
    </div>
  );
};
